import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Button,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Box,
  IconButton,
  Badge,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
  Typography,
  Divider,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import { useBookMyOpAuth } from '../../contexts/BookMyOpAuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import CommonSearch from '../../components/CommonSearch';
import BookMyOpAPI from '../../services/BookMyOpapi';
import { searchAddressPredictions, getPlaceDetails, reverseGeocode, reverseGeocodeFallback } from '../../services/googleMapsService';
import OrganizationTypeFilter from '../../components/OrganizationTypeFilter';
import EasyOPSwitchButton from '../../components/EasyOPSwitchButton';
import { useSafeArea } from '../../hooks/useSafeArea';
import { Capacitor } from '@capacitor/core';
let Geolocation, Permissions;
try { 
  Geolocation = require('@capacitor/geolocation').Geolocation; 
  Permissions = require('@capacitor/permissions').Permissions; 
} catch { 
  Geolocation = undefined;
  Permissions = undefined;
}

const SearchSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout, isAuthenticated, userLocation, refreshProfile } = useBookMyOpAuth();
  const { unreadCount } = useNotifications();
  const safeAreaInsets = useSafeArea();
  const [location, setLocation] = useState('Detect Location');
  const [locationAnchorEl, setLocationAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Load location from user profile on mount or when userLocation changes
  useEffect(() => {
    if (userLocation && userLocation.city) {
      setLocationData(userLocation);
      // Use city from userLocation (which comes from current_city field)
      setLocation(userLocation.city);
      console.log('Loaded user location from profile:', userLocation.city);
    } else {
      setLocation('Detect Location');
      setLocationData(null);
    }
  }, [userLocation]);

  const handleLocationClick = (event) => {
    setLocationAnchorEl(event.currentTarget);
  };

  const handleLocationClose = () => {
    setLocationAnchorEl(null);
  };

  const handleDetectLocation = async () => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login to use location services',
        severity: 'warning'
      });
      handleLocationClose();
      return;
    }

    setIsDetectingLocation(true);
    handleLocationClose();

    const isNative = Capacitor.isNativePlatform && Capacitor.isNativePlatform();
    
    // Use Capacitor Geolocation for native apps
    if (isNative && Geolocation && Permissions) {
      try {
        // Check and request permissions
        let permStatus;
        try {
          permStatus = await Permissions.query({ name: 'geolocation' });
        } catch (e) {
          permStatus = { state: 'prompt' };
        }
        
        if (permStatus.state !== 'granted') {
          try {
            permStatus = await Permissions.request({ name: 'geolocation' });
          } catch (e) {
            setIsDetectingLocation(false);
            setSnackbar({
              open: true,
              message: 'Location permission is required. Please enable it in app settings.',
              severity: 'error'
            });
            return;
          }
        }
        
        if (permStatus.state !== 'granted') {
          setIsDetectingLocation(false);
          setSnackbar({
            open: true,
            message: 'Location permission is required. Please enable it in app settings.',
            severity: 'error'
          });
          return;
        }

        // Get position using Capacitor
        const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
        const latitude = parseFloat(position.coords.latitude.toFixed(6));
        const longitude = parseFloat(position.coords.longitude.toFixed(6));
        
        // Reverse geocode to get address, city, state, and pincode (required for correct API payload)
        let locationDetails = { latitude, longitude };
        try {
          const geocodedData = await reverseGeocode(latitude, longitude);
          locationDetails = {
            latitude: geocodedData.latitude,
            longitude: geocodedData.longitude,
            address: geocodedData.address,
            city: geocodedData.city,
            state: geocodedData.state,
            pincode: geocodedData.pincode
          };
          console.log('📍 Reverse geocoded location:', locationDetails);
        } catch (geocodeError) {
          try {
            const fallbackData = await reverseGeocodeFallback(latitude, longitude);
            locationDetails = {
              latitude: fallbackData.latitude,
              longitude: fallbackData.longitude,
              address: fallbackData.address,
              city: fallbackData.city,
              state: fallbackData.state,
              pincode: fallbackData.pincode
            };
            console.log('📍 Fallback reverse geocoded location:', locationDetails);
          } catch (fallbackError) {
            console.warn('⚠️ Reverse geocoding failed, sending only coordinates:', fallbackError);
          }
        }

        // Save location to backend - send same payload structure as address selection
        const response = await BookMyOpAPI.updateLocation(locationDetails);

        if (response.status === 'success') {
          const data = response.data;
          setLocationData(data);
          
          // Display location: prefer current_city, then city, then locality/area, then fallback
          const locationText = data.current_city 
            ? data.current_city
            : data.city 
            ? data.city
            : data.locality
            ? data.locality
            : data.area
            ? data.area
            : 'Current Location';
          
          setLocation(locationText);
          
          // Refresh profile to update context with new location
          // Wrap in try-catch to prevent errors from breaking the flow
          try {
            await refreshProfile(true);
          } catch (error) {
            console.error('Profile refresh failed, but location was updated:', error);
            // Don't throw - location update was successful
            // The stored user data in localStorage will be updated on next successful profile fetch
          }
          
          setSnackbar({
            open: true,
            message: 'Location updated successfully!',
            severity: 'success'
          });

          // Don't reload - React will handle re-rendering when context updates
          // Components that use userLocation from context will automatically update
        }
      } catch (error) {
        console.error('Failed to detect location:', error);
        setIsDetectingLocation(false);
        setSnackbar({
          open: true,
          message: error?.message || 'Failed to detect location. Please try again.',
          severity: 'error'
        });
      }
    } else if (navigator.geolocation) {
      // Web browser fallback - use same payload structure as native (reverse geocode for address, city, state, pincode)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Round coordinates to 6 decimal places (sufficient accuracy, ~0.11m precision)
            const latitude = parseFloat(position.coords.latitude.toFixed(6));
            const longitude = parseFloat(position.coords.longitude.toFixed(6));

            // Reverse geocode to get address, city, state, and pincode (same as native path)
            let locationDetails = { latitude, longitude };
            try {
              const geocodedData = await reverseGeocode(latitude, longitude);
              locationDetails = {
                latitude: geocodedData.latitude,
                longitude: geocodedData.longitude,
                address: geocodedData.address,
                city: geocodedData.city,
                state: geocodedData.state,
                pincode: geocodedData.pincode
              };
            } catch (geocodeError) {
              try {
                const fallbackData = await reverseGeocodeFallback(latitude, longitude);
                locationDetails = {
                  latitude: fallbackData.latitude,
                  longitude: fallbackData.longitude,
                  address: fallbackData.address,
                  city: fallbackData.city,
                  state: fallbackData.state,
                  pincode: fallbackData.pincode
                };
              } catch (fallbackError) {
                console.warn('⚠️ Reverse geocoding failed, sending only coordinates:', fallbackError);
              }
            }

            // Save location to backend - same payload structure as address selection
            const response = await BookMyOpAPI.updateLocation(locationDetails);

            if (response.status === 'success') {
              const data = response.data;
              setLocationData(data);
              
              // Display location: prefer current_city, then city, then locality/area, then fallback
              const locationText = data.current_city 
                ? data.current_city
                : data.city 
                ? data.city
                : data.locality
                ? data.locality
                : data.area
                ? data.area
                : 'Current Location';
              
              setLocation(locationText);
              
              // Refresh profile to update context with new location
              // Wrap in try-catch to prevent errors from breaking the flow
              try {
                await refreshProfile(true);
              } catch (error) {
                console.error('Profile refresh failed, but location was updated:', error);
                // Don't throw - location update was successful
                // The stored user data in localStorage will be updated on next successful profile fetch
              }
              
              setSnackbar({
                open: true,
                message: 'Location updated successfully!',
                severity: 'success'
              });

              // Don't reload - React will handle re-rendering when context updates
              // Components that use userLocation from context will automatically update
            }
          } catch (error) {
            console.error('Failed to save location:', error, error?.response, error?.message);
            setSnackbar({
              open: true,
              message: 'Failed to update location: ' +
                (error?.response?.data?.message || error?.response?.data?.detail || error?.message || JSON.stringify(error?.response?.data) || 'Unknown error'),
              severity: 'error'
            });
          } finally {
            setIsDetectingLocation(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsDetectingLocation(false);
          setSnackbar({
            open: true,
            message: 'Please enable location access',
            severity: 'error'
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setIsDetectingLocation(false);
      setSnackbar({
        open: true,
        message: 'Geolocation is not supported',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Address search handlers
  const handleAddressSearchChange = (event) => {
    const query = event.target.value;
    setAddressSearch(query);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Reset suggestions and loading state if query is too short
    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      setIsSearchingAddress(false);
      return;
    }

    // Debounce the search
    console.log('🔍 Scheduling address search for:', query);
    searchTimeoutRef.current = setTimeout(() => {
      console.log('🔍 Executing address search for:', query);
      searchAddress(query.trim());
    }, 500);
  };

  const searchAddress = async (query) => {
    if (!query || query.trim().length < 3) {
      setAddressSuggestions([]);
      setIsSearchingAddress(false);
      return;
    }

    setIsSearchingAddress(true);
    console.log('🔍 Searching address:', query);
    
    try {
      const predictions = await searchAddressPredictions(query);
      console.log('✅ Address predictions received:', predictions?.length || 0);
      setAddressSuggestions(predictions || []);
    } catch (error) {
      console.error('❌ Address search error:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      
      if (error.message && error.message.includes('API key not configured')) {
        setSnackbar({
          open: true,
          message: 'Google Maps API key not configured',
          severity: 'error'
        });
      } else if (error.message && error.message.includes('Failed to load Google Maps')) {
        setSnackbar({
          open: true,
          message: 'Failed to load Google Maps. Please check your internet connection.',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to search addresses. Please try again.',
          severity: 'error'
        });
      }
      setAddressSuggestions([]);
    } finally {
      setIsSearchingAddress(false);
      console.log('🏁 Address search completed');
    }
  };

  const handleAddressSelect = async (suggestion) => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login to use location services',
        severity: 'warning'
      });
      return;
    }

    setIsDetectingLocation(true);
    setAddressSearch('');
    setAddressSuggestions([]);

    try {
      // Get place details including coordinates
      const placeDetails = await getPlaceDetails(suggestion.place_id);
      
      // Save location to backend
      // Send parsed city, state, pincode from Google Places to backend
      const response = await BookMyOpAPI.updateLocation({
        latitude: placeDetails.latitude,
        longitude: placeDetails.longitude,
        address: placeDetails.address,
        city: placeDetails.city,
        state: placeDetails.state,
        pincode: placeDetails.pincode
      });

      if (response.status === 'success') {
        const data = response.data;
        
        // Merge backend response with placeDetails for complete location data
        const mergedLocationData = {
          ...data,
          // Use placeDetails if backend didn't return city/state/pincode
          city: data.city || placeDetails.city || null,
          state: data.state || placeDetails.state || null,
          pincode: data.pincode || placeDetails.pincode || null,
        };
        
        setLocationData(mergedLocationData);
        
        // Use city from placeDetails (since backend geocoding might fail)
        // Fallback to backend response, then locality, then default
        const locationText = placeDetails.city 
          ? placeDetails.city
          : data.city 
          ? data.city
          : data.current_city
          ? data.current_city
          : placeDetails.locality
          ? placeDetails.locality
          : 'Location Set';
        
        setLocation(locationText);
        handleLocationClose();
        
        // Refresh profile to update context with new location
        // Wrap in try-catch to prevent errors from breaking the flow
        try {
          await refreshProfile(true);
        } catch (error) {
          console.error('Profile refresh failed, but location was updated:', error);
          // Don't throw - location update was successful
          // The stored user data in localStorage will be updated on next successful profile fetch
        }
        
        setSnackbar({
          open: true,
          message: 'Location updated successfully!',
          severity: 'success'
        });

        // Don't reload - React will handle re-rendering when context updates
        // Components that use userLocation from context will automatically update
      }
    } catch (error) {
      console.error('Failed to save location:', error, error?.response, error?.message);
      setSnackbar({
        open: true,
        message: 'Failed to update location: ' +
          (error?.response?.data?.message || error?.response?.data?.detail || error?.message || JSON.stringify(error?.response?.data) || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleClearSearch = () => {
    setAddressSearch('');
    setAddressSuggestions([]);
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    logout();
    handleMenuClose();
  };

  // Custom search handler for SearchSection
  const handleSearch = (query, results) => {
    console.log('Search performed in SearchSection:', query);
    console.log('Results found:', results.length);
    // You can add custom logic here, such as:
    // - Analytics tracking
    // - Custom result processing
    // - Navigation to search results page
  };

  // Custom result selection handler
  const handleResultSelect = (result) => {
    console.log('Result selected in SearchSection:', result);
    // You can override the default navigation behavior here
    // For example, you might want to track analytics or show a modal
  };


  return (
    <>
   
    
    <Container 
      maxWidth="xxl" 
      sx={{ 
        pt: { 
          xs: `calc(12px + ${safeAreaInsets.top-34}px)`, 
          md: 1.5 
        }, 
        pb: { xs: 0.75, md: 0.75 }, 
        bgcolor: theme.palette.primary.main 
      }}
    >
      {/* Top Row: App Name (Left) and Notification + Hamburger (Right) */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: { xs: 1, md: 0.75 },
        maxWidth: { md: '85%', lg: '80%', xl: '90%' },
        ml: { xs: 0, md: 0, lg: 2, xl: 4 },
      }}>
        {/* App Name */}
        <Typography
          variant="h6"
          sx={{
            color: 'background.paper',
            fontWeight: 700,
            fontSize: { xs: '1rem', md: '1.1rem' },
            letterSpacing: '0.5px',
          }}
        >
          BookMyOP
        </Typography>

        {/* Notification and Hamburger Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.5 }, flexWrap: 'wrap' }}>
          {/* Easy OP Switch Button - Mobile */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <EasyOPSwitchButton variant="mobile" isScrolled={false} />
          </Box>
          
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                fontWeight: 'bold',
                backgroundColor: '#ff4444',
                color: '#ffffff',
                minWidth: '18px',
                height: '18px',
                padding: '0 4px',
              }
            }}
          >
            <IconButton
              onClick={() => navigate('/notifications')}
              sx={{
                color: '#ffffff',
                padding: { xs: '4px', md: '6px' },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.1),
                }
              }}
            >
              <NotificationsIcon sx={{ fontSize: { xs: '1.2rem', md: '1.3rem' } }} />
            </IconButton>
          </Badge>
          
          {/* Hamburger Menu */}
          <IconButton
            onClick={handleMenuClick}
            sx={{
              color: '#ffffff',
              padding: { xs: '4px', md: '6px' },
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              }
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: '1.2rem', md: '1.3rem' } }} />
          </IconButton>
        </Box>
      </Box>

      {/* Second Row: Location and Organization Filter */}
      <Box sx={{
        display: 'flex',
        alignItems: 'start',
        gap: { xs: 1, md: 1.5 },
        maxWidth: { md: '85%', lg: '80%', xl: '90%' },
        ml: { xs: 0, md: 0, lg: 2, xl: 4 },
      }}>
        {/* Location Selector */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: { xs: 1, md: 'unset' }, minWidth: { xs: 0, md: 140 } }}>
          <Typography
            variant="caption"
            sx={{
              color: 'background.paper',
              fontSize: '0.65rem',
              mb: 0.4,
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            Location
          </Typography>
          <Button
            startIcon={
              isDetectingLocation ? (
                <CircularProgress size={14} color="primary" />
              ) : (
                <LocationOnOutlinedIcon color="primary" sx={{ fontSize: '1rem' }} />
              )
            }
            endIcon={!isDetectingLocation && <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />}
            onClick={handleLocationClick}
            disabled={isDetectingLocation}
            sx={{
              minWidth: { xs: 0, md: 140 },
              width: { xs: '100%', md: 'auto' },
              height: { xs: '30px', md: '32px' },
              justifyContent: 'space-between',
              color: theme.palette.text.primary,
              fontSize: { xs: '0.8rem', md: '0.85rem' },
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: { xs: '100px', md: '12px' },
              backgroundColor: theme.palette.background.paper,
              boxShadow: { md: '0 2px 8px rgba(0,0,0,0.08)' },
              border: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
              px: { xs: 1, md: 1.25 },
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: { md: '0 4px 12px rgba(0,0,0,0.12)' },
                borderColor: theme.palette.primary.main,
              },
              '&:disabled': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.secondary,
              }
            }}
          >
            <Box sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '100px', sm: '110px', md: '100px' },
            }}>
              {location}
            </Box>
          </Button>
        </Box>

        {/* Organization Type Filter - Mobile (in second row) */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', flex: { xs: 1, md: 'unset' }, minWidth: { xs: 0, md: 140 } }}>
          <OrganizationTypeFilter variant="mobile" />
        </Box>

        {/* Desktop Search Bar */}
        <Box sx={{ 
          display: { xs: 'none', md: 'block' },
          flex: 1,
          ml: { md: 2, lg: 3, xl: 4 }
        }}>
          <CommonSearch
            placeholder="Search Doctors, Hospitals and more..."
            onSearch={handleSearch}
            onResultSelect={handleResultSelect}
            showResultTypes={true}
            sx={{
              pt: 0,
              pb: 0,
              '& .MuiContainer-root': {
                px: 0,
              }
            }}
          />
        </Box>
      </Box>
      {/* Location Menu */}
      <Menu
        anchorEl={locationAnchorEl}
        open={Boolean(locationAnchorEl)}
        onClose={handleLocationClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            width: { xs: 340, sm: 380 },
            maxWidth: '90vw',
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        {/* Detect Location Option */}
        <MenuItem 
          onClick={handleDetectLocation}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.08),
            }
          }}
        >
          <MyLocationIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: '1.25rem'
            }} 
          />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Detect My Location
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use GPS to find nearby services
            </Typography>
          </Box>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Search Address */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
            OR SEARCH ADDRESS
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search city, area..."
            value={addressSearch}
            onChange={handleAddressSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.1rem' }} />
                </InputAdornment>
              ),
              endAdornment: addressSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                
                fontSize: '0.9rem',
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
        </Box>

        {/* Address Suggestions */}
        {isSearchingAddress && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isSearchingAddress && addressSuggestions.length > 0 && (
          <List sx={{ px: 1, py: 0, maxHeight: 200, overflowY: 'auto' }}>
            {addressSuggestions.map((suggestion, index) => (
              <ListItem key={suggestion.place_id || index} disablePadding>
                <ListItemButton
                  onClick={() => handleAddressSelect(suggestion)}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.5,
                    py: 1,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.08),
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <PlaceIcon sx={{ color: theme.palette.primary.main, fontSize: '1.1rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {suggestion.structured_formatting?.main_text || suggestion.description}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.7rem'
                      }}>
                        {suggestion.structured_formatting?.secondary_text || suggestion.description}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {!isSearchingAddress && addressSearch.length >= 3 && addressSuggestions.length === 0 && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
              No locations found. Try different search.
            </Typography>
          </Box>
        )}

        {/* Current Location Display */}
        {locationData && !addressSearch && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                CURRENT LOCATION
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {(locationData.current_city || locationData.city) && (locationData.current_state || locationData.state)
                  ? `${locationData.current_city || locationData.city}, ${locationData.current_state || locationData.state}`
                  : locationData.current_city || locationData.city || locationData.locality || 'Location detected'}
              </Typography>
              {(locationData.current_pincode || locationData.pincode) && (
                <Typography variant="caption" color="text.secondary">
                  Pincode: {locationData.current_pincode || locationData.pincode}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default SearchSection; 